import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { ConfiguratorPrice } from "../../../components/ConfiguratorPrice";
import { EditorialBadgeChip } from "../../../components/EditorialBadgeChip";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { ProductUnavailable } from "../../../components/ProductUnavailable";
import { RichText } from "../../../components/RichText";
import { ConfiguratorTypes } from "../../../constants";
import { IConcreteBundle, IProduct } from "../../../models/catalogue";
import { FinancingPlans } from "../../../models/financing";
import { IImageURL, ISafeHTML, WebPageURL } from "../../../models/nominals";
import { IAPIPrice, IPrice } from "../../../models/prices";
import { getTotalPriceWithAddons } from "../../../utils/money";
import { usePageSetting } from "../../../utils/settings";
import {
    filterFinancingPlans,
    getLongestFinancingPlan,
    // eslint-disable-next-line import/no-restricted-paths -- pre-existing cross-app dependency (Phase E exemption)
} from "../../checkout/utils";
// eslint-disable-next-line import/no-restricted-paths -- pre-existing cross-app dependency (Phase E exemption)
import { FinancingModalTriggerConfigurator } from "../../financing/FinancingModalTrigger";
import { AddToBasketButton } from "../AddToBasketButton";
import { ConfiguratorDeliveryDisclaimer } from "../ConfiguratorDeliveryDisclaimer";
import { ConfiguratorDynamicshippingTimeMessaging } from "../ConfiguratorDynamicshippingTimeMessaging";
import { ConfiguratorOptionSelectionError } from "../ConfiguratorOptionSelectionError";
import { ConfiguratorOptions } from "../ConfiguratorOptions";
import { ConfiguratorPriceLoading } from "../ConfiguratorPriceLoading";
import { ConfiguratorReviewAggregation } from "../ConfiguratorReviewAggregation";
import { ConfiguratorShippingCallout } from "../ConfiguratorShippingCallout";
import { ConfiguratorUpsells } from "../ConfiguratorUpsells";
import { QuantitySelector } from "../QuantitySelector";
import { RatingsSummary } from "../RatingsSummary";
import {
    IModularConfiguratorOptionSet,
    IUpsellModalComponentFn,
} from "../models";

const AppSlug = constants["tsicommon.types.AppSlug"];

const getRootClassNames = (
    configType: string,
    rootProduct: IProduct | null,
    optionSetLayoutDesign: string,
): string => {
    const brandColor =
        rootProduct && rootProduct.attributes.brand_color
            ? rootProduct.attributes.brand_color.value
            : "";
    return classNames({
        configurator: true,
        [`${configType}__configurator`]: true,
        [`${configType}__configurator--${brandColor}`]:
            optionSetLayoutDesign === ConfiguratorTypes.CLASSIC && !!brandColor,
    });
};

const getProductUPC = (rootProduct: IProduct | null) => {
    return rootProduct && rootProduct.upc ? rootProduct.upc : "";
};

const getChildrenUPCs = (rootProduct: IProduct | null) => {
    const children =
        rootProduct && rootProduct.children ? rootProduct.children : [];
    return children.map((c) => {
        return c.upc;
    });
};

const getLongestActivePlan = (financingPlans: FinancingPlans | undefined) => {
    if (financingPlans) {
        // Before retrieving the longest active financing plan, narrow down the selection to include only plans with a 0% APR
        const filteredFinancingPlans = filterFinancingPlans(financingPlans);
        return getLongestFinancingPlan(filteredFinancingPlans);
    }
    return null;
};

const buildBoxHeader = (boxHeaderContent: string): React.ReactNode => {
    if (!boxHeaderContent) {
        return null;
    }
    return <div className="configurator__top-copy">{boxHeaderContent}</div>;
};

const buildTopBadge = (
    topBadgeContent: ISafeHTML | string,
): React.ReactNode => {
    if (!topBadgeContent) {
        return null;
    }
    return (
        <div className="configurator__new">
            <RichText html={topBadgeContent} tagName="span" />
        </div>
    );
};

const buildOptionSelectors = (
    rootProduct: IProduct | null,
    optionSet: IModularConfiguratorOptionSet,
    chatLinkComponent: React.JSX.Element | null | undefined,
    liveChatHeader: string | undefined,
    financingLink: WebPageURL,
    isPlpPanelStyle: boolean | undefined,
    useCompareDrawerCenter: boolean | undefined,
): React.ReactNode => {
    if (!rootProduct) {
        return null;
    }
    return (
        <ConfiguratorOptions
            optionSet={optionSet}
            chatLinkComponent={chatLinkComponent}
            liveChatHeader={liveChatHeader}
            financingLink={financingLink}
            isPlpPanelStyle={isPlpPanelStyle}
            useCompareDrawerCenter={useCompareDrawerCenter}
        />
    );
};

const buildPrice = (
    price: IAPIPrice | null,
    upgradedVariant: IProduct | null,
    optionSet: IModularConfiguratorOptionSet,
    selectedAddonPrice: IPrice | null,
    financingLink: WebPageURL,
    strikeThroughMSRP: boolean,
    actualPriceStyle: string,
    overrideFinancingCopy: string,
    supDollarSign: boolean | undefined,
    showFinancingModal: boolean | undefined,
    financingPlans: FinancingPlans | undefined,
    priceError: string | null,
): React.ReactNode => {
    if (priceError || !price || !upgradedVariant) {
        return (
            <ConfiguratorPriceLoading
                configuratorType={optionSet.layout.design}
                alternatePriceView={optionSet.layout.enable_prequal}
                errorMessage={priceError}
            />
        );
    }
    return (
        <ConfiguratorPrice
            price={price}
            financingPlans={financingPlans}
            variant={upgradedVariant}
            selectedAddonPrice={selectedAddonPrice}
            configuratorType={optionSet.layout.design}
            financingLink={financingLink}
            strikeThroughMSRP={strikeThroughMSRP}
            discountDisplayMode={optionSet.discounts.discount_mode}
            percentageDiscountFormat={
                optionSet.discounts.percentage_discount_format
            }
            absoluteDiscountFormat={
                optionSet.discounts.absolute_discount_format
            }
            actualPriceStyle={actualPriceStyle}
            overrideFinancingCopy={overrideFinancingCopy}
            supDollarSign={supDollarSign}
            alternatePriceView={
                optionSet.layout.design !== ConfiguratorTypes.PANEL &&
                optionSet.layout.enable_prequal
            }
            showFinancingModal={showFinancingModal}
            pricingDisclaimerTooltipText={
                upgradedVariant?.pricing_disclaimer?.show_disclaimer
                    ? upgradedVariant.pricing_disclaimer.tooltip_text
                    : undefined
            }
        />
    );
};

const buildPreTitle = (
    showPreTitle: boolean,
    baseVariant: IProduct | null,
): React.ReactNode => {
    if (!showPreTitle) {
        return null;
    }
    const pretitle = baseVariant
        ? baseVariant.availability.short_message
        : t`Currently Unavailable`;
    return <h2 className="configurator__pretitle">{pretitle}</h2>;
};

const buildTitle = (title: string | undefined): React.ReactNode => {
    if (!title) {
        return null;
    }
    return <h2 className="configurator__title">{title}</h2>;
};

const buildHeader = (
    preheader: string | undefined,
    title: string | undefined,
    subhead: string | undefined,
    badge: React.ReactNode,
): React.ReactNode => {
    const text = (copy: string | undefined) => {
        if (!copy) {
            return null;
        }
        return <RichText html={copy} />;
    };
    return (
        <div className="configurator__header">
            <span className="configurator__preheader">{text(preheader)}</span>
            {badge}
            {buildTitle(title)}
            {text(subhead)}
        </div>
    );
};

const buildReviewAggregation = (
    optionSet: IModularConfiguratorOptionSet,
): React.ReactNode => {
    if (!optionSet.layout.review_aggregation?.review_aggregation_copy) {
        return null;
    }
    return (
        <ConfiguratorReviewAggregation
            reviewAggregationCopy={
                optionSet.layout.review_aggregation.review_aggregation_copy
            }
            modalContent={optionSet.layout.review_aggregation.modal_content}
            checkmarkIconURL={
                optionSet.layout.review_aggregation.checkmark_icon?.url
            }
            starsImageURL={optionSet.layout.review_aggregation.modal_image?.url}
        />
    );
};

const buildDescription = (
    description: string | undefined,
    learnMoreSelector: string,
    hideLearnMoreLink: boolean | undefined,
): React.ReactNode => {
    if (!description) {
        return null;
    }
    // If an element on the page exists matching the learnMoreSelector prop, include an anchor link to it.
    let learnMoreLink: React.JSX.Element | null = null;
    if (learnMoreSelector && !hideLearnMoreLink) {
        const document = getDocument();
        const learnMoreTarget = document?.querySelector(learnMoreSelector);
        if (learnMoreTarget) {
            learnMoreLink = (
                <a
                    href={`${learnMoreSelector}`}
                    className="configurator__learn-link"
                >
                    {t`Learn More`}
                </a>
            );
        }
    }

    // This will eventually check against the description field for the rootProduct in Oscar. In order to
    // preserve data, we'll use the CMS data in the interim
    const descriptionContent: React.JSX.Element = (
        <span>
            <RichText html={description} tagName="span" />
            {learnMoreLink}
        </span>
    );

    return (
        <div className="configurator__description">{descriptionContent}</div>
    );
};

const buildStarRating = (
    optionSet: IModularConfiguratorOptionSet,
    starRatingURL: WebPageURL | undefined,
    showStarRatingsModal: boolean | undefined,
): React.ReactNode => {
    const isPanel = optionSet.layout.design === ConfiguratorTypes.PANEL;
    if (!optionSet.layout.enable_star_rating) {
        return null;
    }
    return (
        <RatingsSummary
            isPanel={isPanel}
            starRatingURL={starRatingURL}
            showModal={showStarRatingsModal}
        />
    );
};

const buildImage = (
    rootProduct: IProduct | null,
    showImage: boolean | undefined,
): React.ReactNode => {
    if (!rootProduct) {
        return null;
    }

    if (!showImage || !rootProduct.images.length) {
        return null;
    }

    const image = rootProduct.images[0];

    const style = {
        backgroundImage: `url(${image.original})`,
    };

    return <div className="configurator__image" style={style}></div>;
};

const buildPromoCopy = (promoCopy: string | undefined): React.ReactNode => {
    if (!promoCopy) {
        return null;
    }

    return (
        <div className="configurator__promo-copy">
            <RichText html={promoCopy} />
        </div>
    );
};

const buildShippingCallout = (
    optionSet: IModularConfiguratorOptionSet,
    phoneNumber: string | undefined,
): React.ReactNode => {
    if (optionSet.layout.dynamic_shipping_time_messaging) {
        return (
            <ConfiguratorDynamicshippingTimeMessaging
                dynamicShippingTimeMessaging={
                    optionSet.layout.dynamic_shipping_time_messaging
                }
                iconURL={optionSet.layout.shipping_callout_icon?.url}
                modalShippingMessages={optionSet.layout.shipping_message}
                configuratorType={optionSet.layout.design}
                phoneNumber={phoneNumber}
            />
        );
    } else if (optionSet.layout.shipping_callout_content) {
        return (
            <ConfiguratorShippingCallout
                iconURL={optionSet.layout.shipping_callout_icon?.url}
                content={optionSet.layout.shipping_callout_content}
                shippingMessages={optionSet.layout.shipping_message}
                configuratorType={optionSet.layout.design}
                phoneNumber={phoneNumber}
            />
        );
    }
    return null;
};

const buildPromoComponents = (
    optionSet: IModularConfiguratorOptionSet,
    price: IAPIPrice | null,
    selectedAddonPrice: IPrice | null,
    financingPlans: FinancingPlans | undefined,
    upgradedVariant: IProduct | null,
    financingLink: WebPageURL,
): React.ReactNode => {
    if (!optionSet.layout.enable_prequal || !price) {
        return null;
    }
    const totalPriceWithAddons = getTotalPriceWithAddons(
        price.total,
        selectedAddonPrice,
    );
    const longestActivePlan = getLongestActivePlan(financingPlans);
    const planLength = longestActivePlan
        ? longestActivePlan.term_months
        : upgradedVariant?.price.per_month_term_length ||
          totalPriceWithAddons?.per_month_term_length;
    return (
        <FinancingModalTriggerConfigurator
            planLength={planLength}
            financingLink={financingLink}
        />
    );
};

export const Configurator = (
    props: Immutable<{
        optionSet: IModularConfiguratorOptionSet;
        // Basic Data
        title?: string;
        preheader?: string;
        subhead?: string;
        description?: string;
        financingLink: WebPageURL;
        rootProduct: IProduct | null;
        concreteBundles: readonly IConcreteBundle[];
        baseVariant: IProduct | null;
        upgradedVariant: IProduct | null;
        price: IAPIPrice | null;
        priceError: string | null;
        financingPlans?: FinancingPlans;
        showImage?: boolean;
        promoCopy?: string;
        chatLinkComponent?: React.JSX.Element | null;
        liveChatHeader?: string;
        basketLink: WebPageURL;
        configureGiftsLink: WebPageURL;

        showShippingLead?: boolean;
        showUpsellModalIcon?: boolean;
        // Product Add-Ons
        selectedAddonPrice: IPrice | null;

        // Optional: Up Sell Modal functionality
        showUpsellModal?: (product: IProduct | null) => boolean;
        getUpsellModalComponentFn?: (
            product: IProduct,
        ) => IUpsellModalComponentFn | null;

        // Colors
        buttonColor: string;

        // Misc UI options
        configType: string;
        getDeliveryCopy: (
            rootProduct: IProduct,
            isPreorder: boolean,
        ) => string | null;
        getDeliveryIcon?: () => IImageURL | null;
        showFeelScale: boolean;
        showPreTitle: boolean;
        learnMoreSelector: string;
        hideLearnMoreLink?: boolean;
        showVariantCallout: boolean;
        starRatingURL?: WebPageURL;
        strikeThroughMSRP: boolean;
        actualPriceStyle: string;
        boxHeaderContent: string;
        topBadgeContent: ISafeHTML | string;
        overrideFinancingCopy: string;
        supDollarSign?: boolean;
        showStarRatingsModal?: boolean;
        showFinancingModal?: boolean;
        phoneNumber?: string;
        optionSelectionErrorReason?: string;
        showImagePerUpgradeOption: boolean;
        isPlpPanelStyle?: boolean;
        useCompareDrawerCenter?: boolean;
        children?: React.ReactNode;
    }>,
): React.ReactNode => {
    const appSlug = usePageSetting("app_slug") || "";

    const buildConfiguratorContent = (): React.ReactNode => {
        if (!props.rootProduct) {
            return <LoadingSpinner />;
        }
        const isPanel =
            props.optionSet.layout.design === ConfiguratorTypes.PANEL;
        // Render the variant's price and availability information.
        const variantIsAvailableToBuy = !!(
            props.rootProduct &&
            props.upgradedVariant &&
            props.upgradedVariant.availability.is_available_to_buy
        );
        let variantCallout: React.JSX.Element | null = null;
        let variantPrice: React.ReactNode = null;
        let variantUnavailableReason: React.JSX.Element | null = null;
        if (variantIsAvailableToBuy) {
            variantPrice = buildPrice(
                props.price,
                props.upgradedVariant,
                props.optionSet,
                props.selectedAddonPrice,
                props.financingLink,
                props.strikeThroughMSRP,
                props.actualPriceStyle,
                props.overrideFinancingCopy,
                props.supDollarSign,
                props.showFinancingModal,
                props.financingPlans,
                props.priceError,
            );
            if (
                props.upgradedVariant &&
                props.upgradedVariant.attributes.product_callout &&
                props.showVariantCallout
            ) {
                variantCallout = (
                    <div className="configurator__variant-callout">
                        {props.upgradedVariant.attributes.product_callout.value}
                    </div>
                );
            }
        } else {
            variantUnavailableReason = (
                <ProductUnavailable variant={props.upgradedVariant} />
            );
        }

        // Render delivery disclaimer
        const deliveryWindow: React.JSX.Element = (
            <ConfiguratorDeliveryDisclaimer
                configuratorType={props.optionSet.layout.design}
                variantIsAvailableToBuy={variantIsAvailableToBuy}
                rootProduct={props.rootProduct}
                upgradedVariant={props.upgradedVariant}
                showShippingLead={props.showShippingLead}
                phoneNumber={props.phoneNumber}
                getDeliveryCopy={props.getDeliveryCopy}
                getDeliveryIcon={props.getDeliveryIcon}
            />
        );

        const shippingCallout: React.ReactNode = buildShippingCallout(
            props.optionSet,
            props.phoneNumber,
        );

        const starRating = buildStarRating(
            props.optionSet,
            props.starRatingURL,
            props.showStarRatingsModal,
        );

        // Combine all the content together
        return (
            <div className="configurator__content">
                {buildOptionSelectors(
                    props.rootProduct,
                    props.optionSet,
                    props.chatLinkComponent,
                    props.liveChatHeader,
                    props.financingLink,
                    props.isPlpPanelStyle,
                    props.useCompareDrawerCenter,
                )}
                {props.configType !== "micro" && (
                    <ConfiguratorUpsells
                        configuratorType={props.optionSet.layout.design}
                    />
                )}
                {variantCallout}
                {appSlug !== AppSlug.TEMPURCA &&
                    (appSlug === AppSlug.PILLOWS ||
                        !props.showStarRatingsModal) &&
                    props.rootProduct &&
                    !!props.rootProduct.rating &&
                    starRating}
                {!isPanel ? variantPrice : deliveryWindow}
                {!isPanel && variantUnavailableReason}
                {!isPanel ? deliveryWindow : variantPrice}
                {isPanel && variantUnavailableReason}
                {buildPromoCopy(props.promoCopy)}
                {isPanel && (
                    <ConfiguratorOptionSelectionError
                        optionSelectionErrorReason={
                            props.optionSelectionErrorReason
                        }
                    />
                )}
                <div className="configurator__selection">
                    {props.optionSet.layout.enable_quantity_selector && (
                        <QuantitySelector
                            configuratorType={props.optionSet.layout.design}
                        />
                    )}
                    <AddToBasketButton
                        basketLink={props.basketLink}
                        configureGiftsLink={props.configureGiftsLink}
                        configuratorType={props.optionSet.layout.design}
                        enableQuantitySelector={
                            props.optionSet.layout.enable_quantity_selector
                        }
                        buttonColor={props.buttonColor}
                        showUpsellModal={props.showUpsellModal}
                        getUpsellModalComponentFn={
                            props.getUpsellModalComponentFn
                        }
                    />
                </div>
                {shippingCallout}
            </div>
        );
    };

    const configuratorClass = getRootClassNames(
        props.configType,
        props.rootProduct,
        props.optionSet.layout.design,
    );
    const productUPC = getProductUPC(props.rootProduct);
    const childrenUPCJSON = JSON.stringify(getChildrenUPCs(props.rootProduct));

    return (
        <div
            className={configuratorClass}
            data-mpn={productUPC}
            data-children-mpns={childrenUPCJSON}
        >
            {buildBoxHeader(props.boxHeaderContent)}
            <div className="configurator__inner">
                {buildImage(props.rootProduct, props.showImage)}
                {buildTopBadge(props.topBadgeContent)}
                {buildPreTitle(props.showPreTitle, props.baseVariant)}
                {buildHeader(
                    props.preheader,
                    props.title,
                    props.subhead,
                    <EditorialBadgeChip
                        badge={props.rootProduct?.editorial_badge}
                    />,
                )}
                {appSlug !== AppSlug.PILLOWS &&
                    props.showStarRatingsModal &&
                    props.rootProduct &&
                    buildStarRating(
                        props.optionSet,
                        props.starRatingURL,
                        props.showStarRatingsModal,
                    )}
                {buildReviewAggregation(props.optionSet)}
                {buildDescription(
                    props.description,
                    props.learnMoreSelector,
                    props.hideLearnMoreLink,
                )}
                {buildConfiguratorContent()}
                {buildPromoComponents(
                    props.optionSet,
                    props.price,
                    props.selectedAddonPrice,
                    props.financingPlans,
                    props.upgradedVariant,
                    props.financingLink,
                )}
                {props.children}
            </div>
        </div>
    );
};
